import React, { useState } from 'react';
import { subscriptionService, PremiumInterestPayload } from '../services/SubscriptionService';

interface PremiumInterestModalProps {
  isOpen: boolean;
  onClose: () => void;
  context: string;
  devisId?: string;
  articleId?: string;
}

export const PremiumInterestModal: React.FC<PremiumInterestModalProps> = ({
  isOpen,
  onClose,
  context,
  devisId,
  articleId,
}) => {
  const [wouldPay, setWouldPay] = useState('maybe');
  const [usefulness, setUsefulness] = useState('4');
  const [why, setWhy] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    try {
      const payload: PremiumInterestPayload = {
        context,
        devisId,
        articleId,
        answers: {
          wouldPay,
          usefulness,
          why,
        },
        meta: {
          submittedAt: new Date().toISOString(),
        },
      };

      await subscriptionService.sendPremiumInterest(payload);
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Impossible d'enregistrer votre retour");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 max-w-lg w-full shadow-lg">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Decouvrir le Premium</h2>
          <button
            type="button"
            className="text-gray-500 hover:text-gray-700"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="Fermer"
          >
            ×
          </button>
        </div>

        {submitted ? (
          <div className="text-green-700 bg-green-50 border border-green-200 rounded-md p-4">
            Merci pour votre retour ! Nous le prenons en compte pour prioriser les prochaines evolutions.
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Seriez-vous pret a activer Premium ?
              </label>
              <div className="flex gap-3">
                {['yes', 'maybe', 'no'].map((value) => (
                  <label key={value} className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      value={value}
                      checked={wouldPay === value}
                      onChange={(e) => setWouldPay(e.target.value)}
                    />
                    <span className="capitalize">{value}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Utilite percue des fonctionnalites Premium
              </label>
              <select
                className="w-full border rounded-md px-3 py-2"
                value={usefulness}
                onChange={(e) => setUsefulness(e.target.value)}
              >
                {[1, 2, 3, 4, 5].map((value) => (
                  <option key={value} value={value}>
                    {value} / 5
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Qu'est-ce qui vous manquerait le plus ?
              </label>
              <textarea
                className="w-full border rounded-md px-3 py-2"
                rows={3}
                value={why}
                onChange={(e) => setWhy(e.target.value)}
                placeholder="Exemple : visualiser le calepinage, gain matiere estime..."
              />
            </div>

            {error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2">
                {error}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="px-4 py-2 border rounded-md text-gray-700"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Fermer
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-60"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Envoi...' : 'Envoyer'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
